/**
 * What a segment asks about a contact.
 *
 * Still deliberately closed. There is no free-form expression anywhere, one
 * level of nesting, and a fixed set of operators — because this tree is turned
 * into SQL, and every extra shape is another place a user-supplied value could
 * reach the database as something other than a bound parameter.
 *
 * Two things travel from a rule into a query: the *value*, always, and for a
 * custom field the *key*. Both are bound parameters — the key lands in a jsonb
 * lookup (`custom ->> $key`), never in the SQL text. `validateSegment` refuses
 * a key that is not a slug before it can get that far, which is belt and
 * braces rather than the guarantee: the binding is the guarantee.
 *
 * The design also offered "Opens", which needs a tracking pixel; the owner
 * decided against one, so there is no such data and no such filter. A filter
 * with nothing behind it matches nobody and looks broken, so it is absent
 * rather than present and empty.
 */
export function isGroup(child) {
    return 'any' in child;
}
/**
 * Every contact, said explicitly.
 *
 * A segment with no children matches everyone — which is the honest reading of
 * "no conditions", and the same thing a campaign with no segment at all means.
 */
export const EVERYONE = { match: 'all', children: [] };
const FIELDS = [
    'status',
    'suppression',
    'name',
    'email',
    'company',
    'lastSentDays',
    'custom',
];
/** The values `status` may be compared against. Anything else is not a state. */
const STATUSES = ['subscribed', 'unsubscribed', 'cleaned'];
/** The values `suppression` may be compared against. */
const REASONS = ['unsubscribed', 'hard_bounce', 'complaint', 'manual'];
/**
 * Which operators make sense for which field.
 *
 * `at_least` on a company name is not a stricter query, it is a meaningless
 * one — and a meaningless rule quietly matching everything is how a campaign
 * reaches an audience nobody chose. Rejected rather than ignored.
 *
 * `custom` accepts the lot, because the organisation decides what the field
 * holds and the type is not carried on the rule. The cost of that is a rule
 * that is well-formed and useless — "at least" on a text field compares two
 * numbers that are both NaN, which matches nothing. Nothing is the safe
 * direction: it under-sends rather than over-sends.
 *
 * `email` is the one text field without the presence pair. `contacts.email` is
 * `NOT NULL` and an address is what makes a contact a contact, so "email is
 * empty" is a rule that can only ever match nobody — and a rule that always
 * matches nobody is indistinguishable, on screen, from one that is broken.
 */
const ALLOWED = {
    status: ['is', 'is_not'],
    suppression: ['is', 'is_not'],
    name: ['is', 'is_not', 'contains', 'not_contains', 'is_empty', 'is_not_empty'],
    email: ['is', 'is_not', 'contains', 'not_contains'],
    company: ['is', 'is_not', 'contains', 'not_contains', 'is_empty', 'is_not_empty'],
    lastSentDays: ['at_least', 'at_most'],
    custom: [
        'is',
        'is_not',
        'contains',
        'not_contains',
        'is_empty',
        'is_not_empty',
        'at_least',
        'at_most',
    ],
};
/** These ask about presence, so a value would be ignored — and is not required. */
const VALUELESS = ['is_empty', 'is_not_empty'];
/** What a custom field's key may be. Mirrors `contactField`'s rule deliberately. */
const KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
/**
 * Whether a definition can be translated, and what is wrong if not.
 *
 * Returns problems rather than throwing, because the caller is a form: someone
 * building a segment needs to be told which rule is wrong, not handed an
 * exception. The translation to SQL may assume anything this accepts.
 */
export function validateSegment(definition) {
    const problems = [];
    for (const child of definition.children) {
        const rules = isGroup(child) ? child.any : [child];
        for (const rule of rules) {
            const problem = checkRule(rule);
            if (problem !== undefined)
                problems.push({ rule, reason: problem });
        }
    }
    return problems;
}
/**
 * Asked in order, and the order carries meaning: a rule naming a field nothing
 * knows has nothing else worth saying about it, and a comparison that does not
 * apply makes the value moot.
 */
function checkRule(rule) {
    return checkShape(rule) ?? checkValue(rule);
}
/** Which field, which comparison, and — for a custom field — which one. */
function checkShape(rule) {
    if (!FIELDS.includes(rule.field))
        return `"${rule.field}" is not something a segment can ask about`;
    if (!ALLOWED[rule.field].includes(rule.operator)) {
        return `"${rule.field}" is not compared with "${rule.operator.replace(/_/g, ' ')}"`;
    }
    if (rule.field !== 'custom') {
        // A key on a built-in field would be read by nothing and would suggest the
        // rule asks about something it does not.
        return rule.key === undefined ? undefined : `"${rule.field}" does not take a field key`;
    }
    if (rule.key === undefined)
        return 'this rule does not say which field it is about';
    return KEY_PATTERN.test(rule.key) ? undefined : `"${rule.key}" is not a field key`;
}
/** Whether the value means anything for the field and comparison chosen. */
function checkValue(rule) {
    // Presence questions carry no value, and demanding one would make "has no
    // company" impossible to express.
    if (VALUELESS.includes(rule.operator))
        return undefined;
    if (rule.value.trim() === '')
        return 'this rule has no value';
    if (rule.operator === 'at_least' || rule.operator === 'at_most')
        return checkAmount(rule);
    if (rule.field === 'status' && !includes(STATUSES, rule.value)) {
        return `a contact is ${STATUSES.join(', ')} — not "${rule.value}"`;
    }
    if (rule.field === 'suppression' && !includes(REASONS, rule.value)) {
        return `a suppression reason is ${REASONS.join(', ')} — not "${rule.value}"`;
    }
    return undefined;
}
function checkAmount(rule) {
    const amount = Number(rule.value);
    if (!Number.isFinite(amount))
        return 'a number';
    // Negative days would be a window that ends before it starts. A negative
    // custom value is ordinary — a balance, a delta — so only days are refused.
    if (rule.field === 'lastSentDays' && amount < 0) {
        return 'a number of days, and not a negative one';
    }
    return undefined;
}
function includes(values, value) {
    return values.includes(value);
}
//# sourceMappingURL=segmentDefinition.js.map